import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

/**
 * A heading whose letters are a window onto a photograph.
 *
 * Adapted from React Bits' MaskedHeading, with the mechanism kept and the
 * machinery replaced:
 *
 *  - The mask is an SVG <clipPath> of <text> elements, positioned and styled
 *    from the real DOM spans so the clip matches the layout exactly. That
 *    part is load-bearing and is kept as is.
 *  - No gsap. The entrance is the same `wordReveal` keyframe every other word
 *    in the hero uses. One less 30 KB dependency for one tween.
 *  - The media moves by `transform` only. The original also wrote
 *    `style.filter` every frame, which repaints; brightness and saturation
 *    are static here and set once in CSS.
 *  - The loop runs only while the heading is on screen and the tab is
 *    visible, and not at all under prefers-reduced-motion.
 *  - Pointer position arrives as a prop from Hero3D, which already tracks it
 *    behind the (hover: hover) guard. No second listener.
 *  - The text stays solid cream until the image has loaded. If it never
 *    does, the company name is still there.
 *
 * `text` is rendered in one block so the clip can cover it; the parent <h1>
 * supplies the semantics.
 */
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

export default function MaskedHeading({
  text,
  tag: Tag = 'span',
  src,
  srcSet,
  sizes,
  fillScale = 1.18,
  parallax = 12,
  drift = 6,
  pointer,
  motionOK = false,
  revealDelay = 0.25,
  className = '',
}) {
  const rootRef = useRef(null);
  const measureRef = useRef(null);
  const mediaRef = useRef(null);
  const wordRefs = useRef([]);
  const baseRefs = useRef([]);
  const glyphRefs = useRef([]);
  const pointerRef = useRef({ x: 0, y: 0 });
  const [loaded, setLoaded] = useState(false);

  const clipId = `mh-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const words = useMemo(() => String(text).split(/\s+/).filter(Boolean), [text]);

  // Latest pointer without re-subscribing the loop on every mouse move.
  pointerRef.current = motionOK && pointer ? pointer : { x: 0, y: 0 };

  // Copy each word's box and font onto its clip glyph.
  useEffect(() => {
    const root = rootRef.current;
    const measure = measureRef.current;
    if (!root || !measure) return undefined;

    const sync = () => {
      const cs = window.getComputedStyle(measure);
      for (let i = 0; i < wordRefs.current.length; i += 1) {
        const box = wordRefs.current[i];
        const base = baseRefs.current[i];
        const glyph = glyphRefs.current[i];
        if (!box || !base || !glyph) continue;
        glyph.setAttribute('x', String(box.offsetLeft));
        glyph.setAttribute('y', String(base.offsetTop));
        glyph.style.fontFamily = cs.fontFamily;
        glyph.style.fontSize = cs.fontSize;
        glyph.style.fontWeight = cs.fontWeight;
        glyph.style.fontStyle = cs.fontStyle;
        glyph.style.letterSpacing = cs.letterSpacing;
      }
    };

    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(root);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(sync).catch(() => {});
    return () => ro.disconnect();
  }, [words]);

  // Drift + parallax, transform only, on screen only.
  useEffect(() => {
    const root = rootRef.current;
    const media = mediaRef.current;
    if (!root || !media) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const place = (x, y) => {
      const maxX = ((fillScale - 1) / 2) * root.clientWidth;
      const maxY = ((fillScale - 1) / 2) * root.clientHeight;
      media.style.transform = `translate3d(${clamp(x, -maxX, maxX).toFixed(2)}px, ${clamp(y, -maxY, maxY).toFixed(2)}px, 0) scale(${fillScale})`;
    };

    if (reduce) {
      place(0, 0);
      return undefined;
    }

    let raf = 0;
    let visible = false;
    let last = 0;
    let clock = 0;
    const off = { x: 0, y: 0 };

    const frame = (now) => {
      raf = 0;
      if (!visible || document.hidden) return;
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
      last = now;
      clock += dt;

      const p = pointerRef.current;
      const tx = p.x * -parallax + Math.sin(clock * 0.21) * drift;
      const ty = p.y * -parallax + Math.cos(clock * 0.17) * drift * 0.6;
      const ease = 1 - Math.exp(-dt / 0.18);
      off.x += (tx - off.x) * ease;
      off.y += (ty - off.y) * ease;
      place(off.x, off.y);
      raf = requestAnimationFrame(frame);
    };

    const start = () => {
      if (!raf) {
        last = 0;
        raf = requestAnimationFrame(frame);
      }
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) start();
      },
      { rootMargin: '10% 0px' }
    );
    io.observe(root);
    const onVis = () => { if (!document.hidden) start(); };
    document.addEventListener('visibilitychange', onVis);

    place(0, 0);
    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', onVis);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [fillScale, parallax, drift]);

  return (
    <Tag
      ref={rootRef}
      className={`masked-heading ${loaded ? 'is-masked' : ''} ${className}`.trim()}
    >
      <span ref={measureRef} className="masked-heading__measure">
        {words.map((word, i) => (
          <span
            key={`${word}-${i}`}
            ref={(el) => { wordRefs.current[i] = el; }}
            className="masked-heading__word"
          >
            {word}
            <i ref={(el) => { baseRefs.current[i] = el; }} className="masked-heading__baseline" />
          </span>
        ))}
      </span>

      <svg className="masked-heading__defs" aria-hidden="true" focusable="false">
        <defs>
          <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
            {words.map((word, i) => (
              <text key={`${word}-${i}`} ref={(el) => { glyphRefs.current[i] = el; }}>
                {word}
              </text>
            ))}
          </clipPath>
        </defs>
      </svg>

      {/* Reveal layer rises through an overflow mask with the same keyframe
          and delay as the words beside it, so the line enters in step. */}
      <span className="masked-heading__reveal" aria-hidden="true">
        <span className="masked-heading__rise animate-word-reveal" style={{ animationDelay: `${revealDelay}s` }}>
          <span className="masked-heading__clip" style={{ clipPath: `url(#${clipId})` }}>
            <span ref={mediaRef} className="masked-heading__media">
              <img
                className="masked-heading__source"
                src={src}
                srcSet={srcSet}
                sizes={sizes}
                alt=""
                draggable={false}
                decoding="async"
                fetchPriority="low"
                onLoad={() => setLoaded(true)}
              />
            </span>
          </span>
        </span>
      </span>
    </Tag>
  );
}
